import { useEffect, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

interface LocaleTranslationWarning {
  code: string;
  name: string;
  cardCount: number;
  themeCount: number;
}

interface ContentItem {
  id: string;
  type: string;
  card_type: string;
  card_category: string;
  title: string | null;
  body: string;
  capture_type: string | null;
  age_min_months: number | null;
  age_max_months: number | null;
  genders: string[];
  seasons: string[];
  tags: string[];
  active: boolean;
  sort_order: number;
  weight: number;
  frequency: string;
  is_dismissable: boolean;
  priority_days: number;
  is_onboarding: boolean;
  is_seasonal: boolean;
  child_specific: boolean;
  subject?: 'parent_only' | 'any_child' | 'specific_child';
  child_assignment?: 'expand' | 'user_selects';
  viewer: 'anyone' | 'co_parent';
  requires_child: boolean;
  completion_type: string;
  acorn_reward: number;
  reappear_after: number | null;
  stops_on: string;
  trigger_event: string | null;
  feed_priority: number;
  pin_to_top: boolean;
  cta_action: string;
  auto_dismiss_hours: number | null;
  source: string;
  created_at: string;
  updated_at: string;
  translations?: Record<string, Record<string, string | null>> | null;
}

interface CardAnalytic {
  id: string;
  title: string | null;
  card_type: string;
  shown_count: number;
  tapped_count: number;
  completed_count: number;
  swiped_past_count: number;
  dismissed_count: number;
  tapped_rate_pct: number | null;
  completion_rate_pct: number | null;
  skip_rate_pct: number | null;
}

const CARD_TYPES = ["challenge", "tip", "memory", "motivational", "upsell", "mori_message"] as const;
const CAPTURE_TYPES = ["voice", "drawing", "photo", "written"] as const;
const FREQUENCY_OPTIONS = ["always", "daily", "weekly", "occasional", "rare"] as const;
const GENDER_OPTIONS = ["any", "male", "female", "neutral"] as const;
const SEASON_OPTIONS = ["spring", "summer", "autumn", "winter"] as const;
const CONTENT_TYPES = [
  "memory_prompt", "spark_question", "milestone",
  "activity", "tip", "occasion_prompt", "game",
] as const;

const CARD_CATEGORIES = [
  { value: "onboarding",    label: "Onboarding" },
  { value: "birthday",      label: "Birthday & anniversaries" },
  { value: "milestone",     label: "Milestone" },
  { value: "challenge",     label: "Challenge" },
  { value: "upsell",        label: "Upsell" },
  { value: "mori_message",  label: "Mori message" },
  { value: "informational", label: "Tips & motivation" },
] as const;

const REAPPEAR_OPTIONS: { value: number | null; label: string }[] = [
  { value: null, label: "Every session (until stopped)" },
  { value: 1,   label: "Every day" },
  { value: 3,   label: "Every 3 days" },
  { value: 7,   label: "Every week" },
  { value: 30,  label: "Every month" },
  { value: 90,  label: "Every 3 months" },
  { value: 365, label: "Every year" },
];

const STOPS_ON_OPTIONS = [
  { value: "either",  label: "User acts on it or dismisses it" },
  { value: "action",  label: "User acts on it only" },
  { value: "dismiss", label: "User dismisses it only" },
  { value: "never",   label: "Never stop showing" },
] as const;

const CARD_TYPE_COLORS: Record<string, string> = {
  challenge:    "bg-purple-100 text-purple-700",
  tip:          "bg-blue-100 text-blue-700",
  memory:       "bg-amber-100 text-amber-700",
  motivational: "bg-green-100 text-green-700",
  upsell:       "bg-pink-100 text-pink-700",
  mori_message: "bg-orange-100 text-orange-700",
};

const CAPTURE_ICONS: Record<string, string> = {
  voice: "\uD83C\uDFA4",
  drawing: "\uD83C\uDFA8",
  photo: "\uD83D\uDCF7",
  written: "\u270D\uFE0F",
};

// Colors matching src/components/cards/types.ts CARD_COLORS
const PREVIEW_COLORS: Record<string, { bg: string; border: string; title: string; body: string }> = {
  streak:       { bg: "#FAECE7", border: "#F0997B", title: "#712B13", body: "#993C1D" },
  memory:       { bg: "#E1F5EE", border: "#9FE1CB", title: "#085041", body: "#0F6E56" },
  game:         { bg: "#EEEDFE", border: "#AFA9EC", title: "#3C3489", body: "#534AB7" },
  tip:          { bg: "#FDF0EE", border: "#E8A99F", title: "#8B3A2E", body: "#C45B4B" },
  spark:        { bg: "#E6F1FB", border: "#85B7EB", title: "#0C447C", body: "#185FA5" },
  milestone:    { bg: "#EAF3DE", border: "#97C459", title: "#27500A", body: "#3B6D11" },
  theme:        { bg: "#F5F0FF", border: "#C4B8F0", title: "#3C3489", body: "#534AB7" },
  mori_message: { bg: "#FDF5E8", border: "#EBC98C", title: "#7A4A10", body: "#A56A20" },
  challenge:    { bg: "#EEEDFE", border: "#AFA9EC", title: "#3C3489", body: "#534AB7" },
  motivational: { bg: "#EAF3DE", border: "#97C459", title: "#27500A", body: "#3B6D11" },
  upsell:       { bg: "#FEF0F7", border: "#E9A0CC", title: "#8B1452", body: "#C4508A" },
};

const CTA_LABELS: Record<string, string> = {
  capture_memory: "Capture this moment →",
  acknowledge:    "Got it →",
  open_upgrade:   "See options →",
  open_settings:  "Settings →",
  open_mori_chat: "Chat with Mori ✨",
  open_feedback:  "Share with Mori →",
};

const CTA_ACTIONS = [
  { value: "capture_memory", label: "Capture memory (default)" },
  { value: "acknowledge", label: "Acknowledge — mark complete, no capture" },
  { value: "open_settings", label: "Open settings" },
  { value: "open_upgrade", label: "Open upgrade / paywall" },
  { value: "open_mori_chat", label: "Open Mori chat (coming soon)" },
  { value: "open_feedback", label: "Open feedback sheet" },
] as const;

// Placeholder substitutions for card preview
const PREVIEW_PLACEHOLDERS: Record<string, string> = {
  "{{childName}}":   "Emma",
  "{{tribeName}}":   "The Novaks",
  "{{inviterName}}": "Sarah",
  "{{count}}":       "5",
  "[childName]":     "Emma",
};

function applyPreviewPlaceholders(text: string): string {
  let out = text;
  // {{date}} — today's date formatted
  const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  out = out.replace(/\{\{date\}\}/g, today);
  for (const [key, val] of Object.entries(PREVIEW_PLACEHOLDERS)) {
    out = out.replace(new RegExp(key.replace(/[{}[\]]/g, "\\$&"), "g"), val);
  }
  return out;
}

function monthsToYears(m: number | null): string {
  if (m === null || m === undefined) return "-";
  if (m < 12) return `${m}m`;
  return `${Math.floor(m / 12)}y`;
}

const EMPTY_ITEM: Omit<ContentItem, "id" | "created_at" | "updated_at"> = {
  type: "tip",
  card_type: "tip",
  card_category: "informational",
  title: "",
  body: "",
  capture_type: "voice",
  age_min_months: null,
  age_max_months: null,
  genders: [],
  seasons: [],
  tags: [],
  active: true,
  sort_order: 0,
  weight: 10,
  frequency: "occasional",
  is_dismissable: true,
  priority_days: 0,
  is_onboarding: false,
  is_seasonal: false,
  child_specific: false,
  subject: 'any_child',
  child_assignment: 'user_selects',
  viewer: 'anyone',
  requires_child: false,
  completion_type: "memory",
  acorn_reward: 0,
  reappear_after: null,
  stops_on: "either",
  trigger_event: null,
  feed_priority: 50,
  pin_to_top: false,
  cta_action: "capture_memory",
  auto_dismiss_hours: null,
  source: "custom",
};

export default function ContentLibraryEditor() {
  const [items, setItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCardType, setFilterCardType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSource, setFilterSource] = useState("all");

  // Edit panel
  const [editing, setEditing] = useState<ContentItem | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [previewLocale, setPreviewLocale] = useState<"en" | "sk">("en");

  // Per-locale translation coverage warnings
  const [localeWarnings, setLocaleWarnings] = useState<LocaleTranslationWarning[]>([]);

  // Card analytics
  const { data: analyticsData } = useQuery({
    queryKey: ["card-analytics"],
    queryFn: async () => {
      try {
        const { data, error } = await supabase.rpc("get_card_analytics");
        if (error) return [];
        return (data ?? []) as CardAnalytic[];
      } catch {
        return [];
      }
    },
    staleTime: 5 * 60_000,
  });

  const analyticsMap = useMemo(() => {
    const map: Record<string, CardAnalytic> = {};
    (analyticsData ?? []).forEach((a) => {
      map[a.id] = a;
    });
    return map;
  }, [analyticsData]);

  function formatMetrics(cardId: string): string {
    const analytics = analyticsMap[cardId];
    if (!analytics || analytics.shown_count === 0) return "—";
    const tappedRate = analytics.tapped_rate_pct !== null ? Math.round(analytics.tapped_rate_pct) : 0;
    const completionRate = analytics.completion_rate_pct !== null ? Math.round(analytics.completion_rate_pct) : 0;
    const skipRate = analytics.skip_rate_pct !== null ? Math.round(analytics.skip_rate_pct) : 0;
    return `Shown: ${analytics.shown_count} | Tapped: ${tappedRate}% | Completed: ${completionRate}% | Skip: ${skipRate}%`;
  }

  useEffect(() => {
    loadItems();
    loadLocaleWarnings();
  }, []);

  async function loadItems() {
    const { data, error } = await supabase
      .from("content_library")
      .select("*")
      .order("sort_order")
      .order("created_at", { ascending: false });
    if (error) {
      toast.error("Failed to load content library");
      return;
    }
    setItems((data ?? []) as unknown as ContentItem[]);
    setLoading(false);
  }

  async function loadLocaleWarnings() {
    const { data: langs } = await supabase
      .from("languages")
      .select("code, name")
      .eq("status", "active")
      .neq("code", "en")
      .order("sort_order");

    if (!langs?.length) return;

    const { data: cards } = await supabase
      .from("content_library")
      .select("translations")
      .eq("active", true);

    const { data: themes } = await supabase
      .from("collection_templates")
      .select("translations")
      .eq("active", true);

    const warnings: LocaleTranslationWarning[] = langs.map((lang) => {
      const cardCount = (cards ?? []).filter(
        (c) => c.translations && (c.translations as Record<string, unknown>)[lang.code]
      ).length;
      const themeCount = (themes ?? []).filter(
        (th) => th.translations && (th.translations as Record<string, unknown>)[lang.code]
      ).length;
      return { code: lang.code, name: lang.name, cardCount, themeCount };
    });

    setLocaleWarnings(warnings.filter((w) => w.cardCount === 0 || w.themeCount === 0));
  }

  const filtered = useMemo(() => {
    return items.filter((item) => {
      if (search) {
        const s = search.toLowerCase();
        const matchesTitle = item.title?.toLowerCase().includes(s);
        const matchesBody = item.body.toLowerCase().includes(s);
        if (!matchesTitle && !matchesBody) return false;
      }
      if (filterCardType !== "all" && item.card_type !== filterCardType) return false;
      if (filterStatus === "active" && !item.active) return false;
      if (filterStatus === "inactive" && item.active) return false;
      if (filterSource !== "all" && item.source !== filterSource) return false;
      return true;
    });
  }, [items, search, filterCardType, filterStatus, filterSource]);

  async function toggleActive(item: ContentItem) {
    const next = !item.active;
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, active: next } : i)));
    const { error } = await supabase
      .from("content_library")
      .update({ active: next, updated_at: new Date().toISOString() })
      .eq("id", item.id);
    if (error) {
      toast.error("Failed to toggle item");
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, active: !next } : i)));
    }
  }

  function openEdit(item: ContentItem) {
    setEditing({ ...item });
    setIsNew(false);
  }

  function openNew() {
    setEditing({ ...EMPTY_ITEM, id: "", created_at: "", updated_at: "" } as ContentItem);
    setIsNew(true);
  }

  function duplicateItem(item: ContentItem) {
    setEditing({
      ...item,
      id: "",
      title: (item.title ?? "") + " (copy)",
      source: "custom",
      created_at: "",
      updated_at: "",
    });
    setIsNew(true);
  }

  async function saveEdit() {
    if (!editing) return;
    const now = new Date().toISOString();
    const payload = {
      type: editing.type,
      card_type: editing.card_type,
      card_category: editing.card_category,
      title: editing.title || null,
      body: editing.body,
      capture_type: editing.capture_type,
      age_min_months: editing.age_min_months,
      age_max_months: editing.age_max_months,
      genders: editing.genders,
      seasons: editing.seasons,
      tags: editing.tags,
      active: editing.active,
      sort_order: editing.sort_order,
      weight: editing.weight,
      frequency: editing.frequency,
      is_dismissable: editing.is_dismissable,
      priority_days: editing.priority_days,
      is_onboarding: editing.is_onboarding,
      is_seasonal: editing.is_seasonal,
      child_specific: editing.subject === 'specific_child',
      subject: editing.subject || 'any_child',
      child_assignment: editing.child_assignment || 'user_selects',
      viewer: editing.viewer || 'anyone',
      requires_child: editing.requires_child,
      completion_type: editing.completion_type,
      acorn_reward: editing.acorn_reward,
      reappear_after: editing.reappear_after ?? null,
      stops_on: editing.stops_on,
      trigger_event: editing.trigger_event,
      feed_priority: editing.feed_priority,
      pin_to_top: editing.pin_to_top,
      cta_action: editing.cta_action,
      auto_dismiss_hours: editing.auto_dismiss_hours,
      source: editing.source,
      updated_at: now,
    };

    if (isNew) {
      const { data, error } = await supabase
        .from("content_library")
        .insert(payload)
        .select()
        .single();
      if (error) {
        toast.error("Failed to create item");
        return;
      }
      setItems((prev) => [data as unknown as ContentItem, ...prev]);
      toast.success("Content item created");
    } else {
      const { error } = await supabase
        .from("content_library")
        .update(payload)
        .eq("id", editing.id);
      if (error) {
        toast.error("Failed to save item");
        return;
      }
      setItems((prev) =>
        prev.map((i) => (i.id === editing.id ? { ...editing, ...payload } : i))
      );
      toast.success("Content item saved");
    }
    setEditing(null);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-12">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      {/* Main list — hidden while editing to give space to form+preview */}
      {!editing && (
        <div className="flex-1 overflow-y-auto">
          {/* Locale translation coverage warnings */}
          {localeWarnings.length > 0 && (
            <div className="px-6 pt-4">
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 space-y-2">
                <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Translation coverage warnings</p>
                <div className="flex flex-wrap gap-2">
                  {localeWarnings.map((w) => (
                    <span key={w.code} className="inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-300 px-3 py-1 text-xs text-amber-800">
                      ⚠ {w.name}:{w.cardCount === 0 ? ` 0 cards translated` : ""}{w.cardCount === 0 && w.themeCount === 0 ? ", " : ""}{w.themeCount === 0 ? ` 0 collections translated` : ""} — invisible to {w.name} users
                    </span>
                  ))}
                </div>
                <p className="text-xs text-amber-700">To translate missing content, run:</p>
                <pre className="rounded bg-amber-100 border border-amber-200 px-3 py-2 text-xs text-amber-900 font-mono overflow-x-auto">
                  {`npx tsx scripts/i18n-translate-card.ts\nnpx tsx scripts/i18n-translate-theme.ts`}
                </pre>
              </div>
            </div>
          )}

          {/* Toolbar */}
          <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h1 className="text-xl font-bold text-gray-900">Content Library</h1>
                <p className="text-sm text-gray-500 mt-0.5">{filtered.length} items</p>
              </div>
              <button
                onClick={openNew}
                className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 transition-colors"
              >
                + Add new
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search title or body..."
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <select
                value={filterCardType}
                onChange={(e) => setFilterCardType(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All types</option>
                {CARD_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <select
                value={filterSource}
                onChange={(e) => setFilterSource(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All sources</option>
                <option value="system">System</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="px-6 pb-8">
            <table className="admin-table mt-2">
              <thead>
                <tr>
                  <th>Active</th>
                  <th>Card Type</th>
                  <th>Category</th>
                  <th>Title</th>
                  <th>Body</th>
                  <th>Age</th>
                  <th>Capture</th>
                  <th>Weight</th>
                  <th>Freq</th>
                  <th>Analytics</th>
                  <th>i18n</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <Switch
                        checked={item.active}
                        onCheckedChange={() => toggleActive(item)}
                      />
                    </td>
                    <td>
                      <span
                        className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          CARD_TYPE_COLORS[item.card_type] ?? "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {item.card_type}
                      </span>
                    </td>
                    <td className="text-xs text-gray-500">
                      {item.card_category ?? <span className="text-gray-300">—</span>}
                    </td>
                    <td className="max-w-[180px] truncate text-sm font-medium text-gray-900">
                      {item.title || <span className="text-gray-400 italic">untitled</span>}
                    </td>
                    <td className="max-w-[240px] truncate text-sm text-gray-600">
                      {item.body.length > 60 ? item.body.slice(0, 60) + "..." : item.body}
                    </td>
                    <td className="text-sm text-gray-600 whitespace-nowrap">
                      {item.age_min_months !== null || item.age_max_months !== null
                        ? `${monthsToYears(item.age_min_months)}-${monthsToYears(item.age_max_months)}`
                        : "-"}
                    </td>
                    <td className="text-center">
                      {item.capture_type ? CAPTURE_ICONS[item.capture_type] ?? item.capture_type : "-"}
                    </td>
                    <td>
                      <span className="inline-block rounded bg-gray-100 px-2 py-0.5 text-xs font-mono">
                        {item.weight}
                      </span>
                    </td>
                    <td>
                      <span className="inline-block rounded bg-gray-100 px-2 py-0.5 text-xs">
                        {item.frequency}
                      </span>
                    </td>
                    <td className="text-xs text-gray-600 whitespace-nowrap">
                      {formatMetrics(item.id)}
                    </td>
                    <td className="whitespace-nowrap text-sm">
                      {["sk", "cs", "de", "es", "fr"].map((loc) => (
                        <span key={loc} title={loc} className="mr-0.5">
                          {item.translations?.[loc] ? "🟢" : "🔴"}
                        </span>
                      ))}
                    </td>
                    <td className="whitespace-nowrap">
                      <button
                        onClick={() => openEdit(item)}
                        className="text-xs text-orange-600 hover:underline mr-3"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => duplicateItem(item)}
                        className="text-xs text-gray-500 hover:underline"
                      >
                        Duplicate
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={12} className="text-center text-gray-400 py-8">
                      No items match your filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <p className="mt-6 text-xs text-gray-400">
              System content (source='system') is managed via migrations.
              Custom content (source='custom') is managed here.
              Rows are never deleted — use the toggle to disable.
            </p>
          </div>
        </div>
      )}

      {/* Edit mode: form (60%) + preview (40%) */}
      {editing && (
        <div className="flex w-full h-full overflow-hidden">
          {/* Back to list button */}
          <div className="flex w-full">
            {/* Form panel — 60% */}
            <div className="w-[60%] border-r border-gray-200 overflow-y-auto bg-white">
              <EditPanel
                item={editing}
                isNew={isNew}
                onChange={setEditing}
                onSave={saveEdit}
                onCancel={() => setEditing(null)}
              />
            </div>
            {/* Preview panel — 40% */}
            <div className="w-[40%] overflow-y-auto bg-gray-50">
              <PreviewPanel
                item={editing}
                locale={previewLocale}
                onLocaleChange={setPreviewLocale}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Preview Panel ─────────────────────────────────────────────────────────────

function PreviewPanel({
  item,
  locale,
  onLocaleChange,
}: {
  item: ContentItem;
  locale: "en" | "sk";
  onLocaleChange: (l: "en" | "sk") => void;
}) {
  const colors = PREVIEW_COLORS[item.card_type] ?? PREVIEW_COLORS.tip;

  // Resolve text for the selected locale
  const translation = locale !== "en" ? (item.translations?.[locale] ?? null) : null;
  const hasSKTranslation = !!(item.translations?.["sk"]);
  const showMissingWarning = locale === "sk" && !hasSKTranslation;

  const resolvedTitle = (translation?.title ?? item.title) ?? null;
  const resolvedBody = translation?.body ?? item.body;

  const displayTitle = resolvedTitle ? applyPreviewPlaceholders(resolvedTitle) : null;
  const displayBody = applyPreviewPlaceholders(resolvedBody);

  const ctaLabel = CTA_LABELS[item.cta_action] ?? "Capture this moment →";

  const categoryLabel = CARD_CATEGORIES.find((c) => c.value === item.card_category)?.label ?? item.card_category;
  const reappearLabel = REAPPEAR_OPTIONS.find((o) => o.value === item.reappear_after)?.label ?? "Every session";
  const stopsOnLabel = STOPS_ON_OPTIONS.find((o) => o.value === item.stops_on)?.label ?? item.stops_on;

  return (
    <div className="p-5 flex flex-col gap-4">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold text-gray-900 uppercase tracking-wide">Preview</p>
        <p className="text-xs text-gray-400 mt-0.5">How this card looks to users</p>
      </div>

      {/* Locale switcher */}
      <div className="flex items-center gap-1">
        {(["en", "sk"] as const).map((loc) => (
          <button
            key={loc}
            onClick={() => onLocaleChange(loc)}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              locale === loc
                ? "bg-orange-100 text-orange-700 border border-orange-300"
                : "bg-white text-gray-500 border border-gray-200 hover:bg-gray-50"
            }`}
          >
            {loc.toUpperCase()}
          </button>
        ))}
        {showMissingWarning && (
          <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-300 px-2.5 py-0.5 text-xs text-amber-700">
            ⚠ Missing SK translation
          </span>
        )}
      </div>

      {/* Card preview */}
      <div className="flex justify-center">
        <div
          style={{
            backgroundColor: colors.bg,
            border: `1px solid ${colors.border}`,
            borderRadius: 16,
            padding: 18,
            width: "100%",
            maxWidth: 320,
            display: "flex",
            flexDirection: "column",
            minHeight: 240,
            maxHeight: 320,
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* Card content */}
          <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            {/* Label row */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              {item.card_type === "mori_message" && (
                <span style={{ fontSize: 16 }}>🌰</span>
              )}
              <span style={{
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: colors.body,
                fontWeight: 600,
              }}>
                {item.card_type === "mori_message" ? "Mori" : item.card_type}
              </span>
            </div>

            {/* Title */}
            {displayTitle && (
              <p style={{
                fontSize: 15,
                fontWeight: 500,
                color: colors.title,
                lineHeight: 1.3,
                marginBottom: 6,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              } as React.CSSProperties}>
                {displayTitle}
              </p>
            )}

            {/* Body */}
            <p style={{
              fontSize: 13,
              lineHeight: 1.5,
              color: colors.body,
              flex: 1,
              display: "-webkit-box",
              WebkitLineClamp: displayTitle ? 5 : 7,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            } as React.CSSProperties}>
              {displayBody || <span style={{ color: "#aaa", fontStyle: "italic" }}>No body text</span>}
            </p>
          </div>

          {/* CTA */}
          <div style={{ paddingTop: 12 }}>
            <div style={{
              backgroundColor: colors.title,
              color: colors.bg,
              fontSize: 14,
              fontWeight: 500,
              padding: "10px 0",
              borderRadius: 24,
              textAlign: "center",
            }}>
              {ctaLabel}
            </div>
          </div>
        </div>
      </div>

      {/* Meta info */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 text-xs text-gray-600 space-y-2">
        <div className="flex justify-between">
          <span className="text-gray-400">Category</span>
          <span className="font-medium text-gray-700">{categoryLabel}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-400">Priority</span>
          <span className="font-medium text-gray-700">{item.feed_priority}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-400">Reappear</span>
          <span className="font-medium text-gray-700">{reappearLabel}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-400">Stops on</span>
          <span className="font-medium text-gray-700">{stopsOnLabel}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-400">Requires child</span>
          <span className={`font-medium ${item.requires_child ? "text-amber-600" : "text-gray-700"}`}>
            {item.requires_child ? "Yes — hides for childless tribes" : "No"}
          </span>
        </div>
        {item.trigger_event && (
          <div className="flex justify-between">
            <span className="text-gray-400">Trigger</span>
            <span className="font-mono text-gray-700">{item.trigger_event}</span>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-400">
        Placeholders used: <span className="font-mono">Emma</span> for childName,{" "}
        <span className="font-mono">The Novaks</span> for tribeName.
        Preview updates live — no save required.
      </p>
    </div>
  );
}

// ─── Edit Panel ────────────────────────────────────────────────────────────────

function EditPanel({
  item,
  isNew,
  onChange,
  onSave,
  onCancel,
}: {
  item: ContentItem;
  isNew: boolean;
  onChange: (item: ContentItem) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  function set<K extends keyof ContentItem>(key: K, val: ContentItem[K]) {
    onChange({ ...item, [key]: val });
  }

  return (
    <div className="overflow-y-auto">
      <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between z-10">
        <h2 className="text-sm font-semibold text-gray-900">
          {isNew ? "New content item" : "Edit content item"}
        </h2>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-lg">
          &times;
        </button>
      </div>

      {item.source === "system" && !isNew && (
        <div className="mx-5 mt-4 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
          This is system content — changes may be overwritten by migrations
        </div>
      )}

      {!isNew && (
        <div className="mx-5 mt-4 rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-600">
          <p className="font-medium text-gray-700 mb-1.5">Translations</p>
          <div className="flex flex-wrap gap-2 mb-2">
            {[
              { code: "sk", name: "Slovak" },
              { code: "cs", name: "Czech" },
              { code: "de", name: "German" },
              { code: "es", name: "Spanish" },
              { code: "fr", name: "French" },
            ].map(({ code, name }) => (
              <span key={code} className="flex items-center gap-1">
                {item.translations?.[code] ? "🟢" : "🔴"}
                <span>{name}</span>
              </span>
            ))}
          </div>
          <p className="text-gray-400 font-mono">
            npm run i18n:translate-card -- --locale sk --card {item.id}
          </p>
        </div>
      )}

      <div className="p-5 space-y-4">
        <Field label="Title">
          <input
            type="text"
            value={item.title ?? ""}
            onChange={(e) => set("title", e.target.value)}
            className="admin-input"
          />
        </Field>
        <Field label="Body">
          <textarea
            value={item.body}
            onChange={(e) => set("body", e.target.value)}
            rows={4}
            className="admin-input resize-y"
          />
          <p className="text-xs text-gray-400 mt-1">Use {"{{childName}}"} or [childName] for child name placeholder</p>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <select value={item.type} onChange={(e) => set("type", e.target.value)} className="admin-input">
              {CONTENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Card type">
            <select value={item.card_type} onChange={(e) => set("card_type", e.target.value)} className="admin-input">
              {CARD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Capture type">
          <select
            value={item.capture_type ?? "voice"}
            onChange={(e) => set("capture_type", e.target.value)}
            className="admin-input"
          >
            {CAPTURE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Age min (months)">
            <input
              type="number"
              value={item.age_min_months ?? ""}
              onChange={(e) => set("age_min_months", e.target.value ? parseInt(e.target.value) : null)}
              className="admin-input"
              placeholder="0"
            />
            {item.age_min_months !== null && (
              <p className="text-xs text-gray-400 mt-0.5">= {monthsToYears(item.age_min_months)}</p>
            )}
          </Field>
          <Field label="Age max (months)">
            <input
              type="number"
              value={item.age_max_months ?? ""}
              onChange={(e) => set("age_max_months", e.target.value ? parseInt(e.target.value) : null)}
              className="admin-input"
              placeholder="216"
            />
            {item.age_max_months !== null && (
              <p className="text-xs text-gray-400 mt-0.5">= {monthsToYears(item.age_max_months)}</p>
            )}
          </Field>
        </div>

        <Field label="Genders">
          <div className="flex flex-wrap gap-2">
            {GENDER_OPTIONS.map((g) => (
              <label key={g} className="flex items-center gap-1.5 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={item.genders.includes(g)}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...item.genders, g]
                      : item.genders.filter((x) => x !== g);
                    set("genders", next);
                  }}
                  className="accent-orange-500"
                />
                {g}
              </label>
            ))}
          </div>
        </Field>

        <Field label="Seasons">
          <div className="flex flex-wrap gap-2">
            {SEASON_OPTIONS.map((s) => (
              <label key={s} className="flex items-center gap-1.5 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={item.seasons.includes(s)}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...item.seasons, s]
                      : item.seasons.filter((x) => x !== s);
                    set("seasons", next);
                  }}
                  className="accent-orange-500"
                />
                {s}
              </label>
            ))}
          </div>
        </Field>

        <Field label="Tags (comma-separated)">
          <input
            type="text"
            value={item.tags.join(", ")}
            onChange={(e) =>
              set(
                "tags",
                e.target.value
                  .split(",")
                  .map((t) => t.trim())
                  .filter(Boolean)
              )
            }
            className="admin-input"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Weight (1-100)">
            <input
              type="number"
              min={1}
              max={100}
              value={item.weight}
              onChange={(e) => set("weight", parseInt(e.target.value) || 10)}
              className="admin-input"
            />
          </Field>
          <Field label="Frequency">
            <select value={item.frequency} onChange={(e) => set("frequency", e.target.value)} className="admin-input">
              {FREQUENCY_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Sort order">
            <input
              type="number"
              value={item.sort_order}
              onChange={(e) => set("sort_order", parseInt(e.target.value) || 0)}
              className="admin-input"
            />
          </Field>
          <Field label="Priority days">
            <input
              type="number"
              value={item.priority_days}
              onChange={(e) => set("priority_days", parseInt(e.target.value) || 0)}
              className="admin-input"
            />
          </Field>
        </div>

        <div className="space-y-3 pt-1">
          <ToggleRow label="Dismissable" checked={item.is_dismissable} onChange={(v) => set("is_dismissable", v)} />
          <ToggleRow label="Onboarding" checked={item.is_onboarding} onChange={(v) => set("is_onboarding", v)} />
          <ToggleRow label="Seasonal" checked={item.is_seasonal} onChange={(v) => set("is_seasonal", v)} />
        </div>

        <Field label="Card subject">
          <select
            value={item.subject || 'any_child'}
            onChange={(e) => set("subject", e.target.value as 'parent_only' | 'any_child' | 'specific_child')}
            className="admin-input"
          >
            <option value="parent_only">Parent only</option>
            <option value="any_child">Any child (user picks at capture)</option>
            <option value="specific_child">Specific child (engine assigns)</option>
          </select>
          <p className="text-xs text-gray-400 mt-1">Who is the subject of this card?</p>
        </Field>

        {(item.subject || 'any_child') === 'specific_child' && (
          <Field label="Child assignment">
            <select
              value={item.child_assignment || 'user_selects'}
              onChange={(e) => set("child_assignment", e.target.value as 'expand' | 'user_selects')}
              className="admin-input"
            >
              <option value="expand">One card per child</option>
              <option value="user_selects">One card, user picks</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">How should this card be assigned to children?</p>
          </Field>
        )}

        <Field label="Viewer">
          <select
            value={item.viewer || 'anyone'}
            onChange={(e) => set("viewer", e.target.value as 'anyone' | 'co_parent')}
            className="admin-input"
          >
            <option value="anyone">Anyone</option>
            <option value="co_parent">Only when a co-parent is in the Family Circle</option>
          </select>
          <p className="text-xs text-gray-400 mt-1">Who should see this card?</p>
        </Field>

        <Field label="Completion type">
          <select
            value={item.completion_type}
            onChange={(e) => set("completion_type", e.target.value)}
            className="admin-input"
          >
            <option value="memory">Memory required</option>
            <option value="acknowledgement">Acknowledgement only</option>
          </select>
          <p className="text-xs text-gray-400 mt-1">How does a user complete this card?</p>
        </Field>

        <Field label="Acorns on completion">
          <input
            type="number"
            min={0}
            max={100}
            step={1}
            value={item.acorn_reward}
            onChange={(e) => set("acorn_reward", Math.max(0, parseInt(e.target.value) || 0))}
            className="admin-input"
          />
          <p className="text-xs text-gray-400 mt-1">Acorns awarded when this card is completed. Use 0 for no reward.</p>
        </Field>

        {/* ── Mori message card fields ─────────────────────────────────────── */}

        <Field label="Trigger event">
          <input
            type="text"
            value={item.trigger_event ?? ""}
            placeholder="e.g. child.birthday.today"
            onChange={(e) => set("trigger_event", e.target.value.trim() || null)}
            className="admin-input"
          />
          <p className="text-xs text-gray-400 mt-1">
            Leave empty for regular feed cards. Set to a MoriEventName to
            create a Mori message card triggered by that event.
          </p>
        </Field>

        <ToggleRow
          label="Pin to top of feed"
          checked={item.pin_to_top}
          onChange={(v) => set("pin_to_top", v)}
        />
        <p className="text-xs text-gray-400 -mt-2">
          Always shown above regular cards. Use for Mori messages.
        </p>

        <Field label="CTA action">
          <select
            value={item.cta_action}
            onChange={(e) => set("cta_action", e.target.value)}
            className="admin-input"
          >
            {CTA_ACTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Auto-dismiss after (hours)">
          <input
            type="number"
            min={1}
            step={1}
            value={item.auto_dismiss_hours ?? ""}
            placeholder="never"
            onChange={(e) => {
              const v = e.target.value;
              set("auto_dismiss_hours", v === "" ? null : Math.max(1, parseInt(v) || 1));
            }}
            className="admin-input"
          />
          <p className="text-xs text-gray-400 mt-1">
            Leave empty to never auto-dismiss. Set 24 for birthday messages etc.
          </p>
        </Field>

        {/* ── Card behaviour section ───────────────────────────────────────── */}

        <div className="border-t border-gray-100 pt-4">
          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
            Card behaviour
          </p>
          <div className="space-y-4">

            <Field label="Category">
              <select
                value={item.card_category}
                onChange={(e) => set("card_category", e.target.value)}
                className="admin-input"
              >
                {CARD_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </Field>

            <Field label="Reappear every">
              <select
                value={item.reappear_after ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  set("reappear_after", v === "" ? null : parseInt(v));
                }}
                className="admin-input"
              >
                {REAPPEAR_OPTIONS.map((o) => (
                  <option key={o.value ?? "null"} value={o.value ?? ""}>{o.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                How soon after being shown should this card be eligible to appear again?
              </p>
            </Field>

            <Field label="Stop showing when">
              <select
                value={item.stops_on}
                onChange={(e) => set("stops_on", e.target.value)}
                className="admin-input"
              >
                {STOPS_ON_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                What makes this card disappear forever for a user?
              </p>
            </Field>

            <ToggleRow
              label="Only show if tribe has children"
              checked={item.requires_child}
              onChange={(v) => set("requires_child", v)}
            />
            <p className="text-xs text-gray-400 -mt-2">
              Hide this card for tribes with no children added yet.
            </p>

            <Field label="Priority (0–100)">
              <input
                type="number"
                min={0}
                max={100}
                step={10}
                value={item.feed_priority}
                onChange={(e) => set("feed_priority", Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
                className="admin-input"
              />
              <p className="text-xs text-gray-400 mt-1">
                Higher priority cards are shown first when multiple cards qualify. 0 = lowest, 50 = normal, 100 = urgent.
              </p>
            </Field>

          </div>
        </div>

        <div className="flex gap-3 pt-4 border-t border-gray-100">
          <button
            onClick={onSave}
            className="flex-1 rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 transition-colors"
          >
            {isNew ? "Create" : "Save"}
          </button>
          <button
            onClick={onCancel}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (val: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between">
      <span className="text-sm text-gray-700">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}
